// The design-language recipes this page uses, as shared constants.
//
// The guide (§6/§7) permits either the globals.css shorthand classes or the
// literal inline recipes, and forbids inventing a third variant. Several of the
// shorthands don't fit here — `.surface` is `rounded-sm` with a softer shadow
// where the comps want 12–16px radii, and `.eyebrow` is 12px `text-wax` where
// the comps want 10px at wider tracking — so this page uses the recipes. Naming
// them once makes "the same recipe everywhere" mechanically true instead of
// aspirational, and gives one place to retune.

/** House card frame. Fields swap `bg-field`; tinted cards swap the wash. */
export const HOUSE_FRAME = "border-[1.5px] border-hairline-bold bg-card shadow-card";

/** Section kicker. 10px is this page's size; the comps use 0.18em tracking. */
export const EYEBROW =
  "font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-wax";

/** The quieter kicker, for meta lines under the tab bar and inside tiles. */
export const EYEBROW_QUIET =
  "font-mono text-[10px] uppercase tracking-smallcaps text-ink-faint";

/** The gold "Free"/format note. */
export const EYEBROW_FREE =
  "font-mono text-[10px] uppercase tracking-smallcaps text-foil";

/**
 * The one solid terracotta CTA. At these heights `rounded-full` matches the
 * comps' 24px pill exactly; this page uses pills rather than `rounded-btn`
 * because every action in the comps is a pill.
 */
export const CTA_PRIMARY =
  "inline-flex items-center gap-2 rounded-full border-[1.5px] border-wax bg-terracotta " +
  "px-6 py-3 font-body text-[16.5px] font-semibold text-paper shadow-btn-terra " +
  "transition-all duration-200 ease-observatory hover:bg-gold-300 active:translate-y-[1px]";

/** Secondary action pill — outlined, never filled. Used for every per-book
 *  action so the one solid CTA per view stays unambiguous. */
export const CTA_SECONDARY =
  "inline-flex items-center gap-2 rounded-full border-[1.5px] border-hairline-bold bg-card " +
  "px-5 py-3 font-body text-[16.5px] text-ink-dim " +
  "transition-all duration-200 ease-observatory hover:border-terracotta hover:text-terracotta";

/** The outlined variant of the closing CTA, for views whose primary action is
 *  reading a featured book rather than building one. */
export const CTA_OUTLINED =
  "inline-flex items-center gap-2 rounded-full border-[1.5px] border-terracotta bg-card " +
  "px-6 py-3 font-body text-[16.5px] font-semibold text-terracotta " +
  "transition-all duration-200 ease-observatory hover:bg-terracotta-wash active:translate-y-[1px]";

/** Quiet mono text link — "See all", "Open the collection", "Clear". */
export const QUIET_LINK =
  "font-mono text-[10px] uppercase tracking-smallcaps text-ink-dim " +
  "transition-colors duration-200 ease-observatory hover:text-terracotta";

/** Section h2. The comps set these at 36px/1.0 across all four tabs. */
export const SECTION_TITLE =
  "font-serif text-[26px] sm:text-[36px] font-normal leading-none text-ink-body";

/** The line under a section title. */
export const SECTION_SUBTITLE = "mt-1 font-body text-[16.5px] leading-snug text-ink-faint";

/**
 * Card titles live inside `<Link>`s, and globals.css sets `a { color:
 * terracotta }` globally — so every one needs an explicit ink colour or it
 * turns orange. Body also carries `line-height: var(--rule)` (25.6px), so tight
 * lines need an explicit leading. Both are baked in here.
 */
export const CARD_TITLE = "font-serif text-[20px] leading-[1.12] text-ink-body";
export const CARD_META = "font-body text-[15px] leading-snug text-ink-faint";

/** Two-line reservation for card titles, so a long real title can't break the
 *  grid rhythm and a short one can't collapse the cell. */
export const CARD_TITLE_CLAMP = "line-clamp-2 min-h-[2.24em]";

/**
 * For display type that contains a LIVE NUMBER — "All 164 books", "The 165th book
 * on this shelf could be yours".
 *
 * `globals.css` sets `font-feature-settings: "onum"` on body, which is right for
 * running prose but renders digits as old-style text figures. At 44px Cormorant a
 * `0` then reads as a lowercase "o" ("All o books"), and a `1` as a short stroke.
 * `lining-nums` restores full-height figures; `tabular-nums` keeps a changing
 * count from shifting the words after it.
 */
export const DISPLAY_NUMERALS = "lining-nums tabular-nums";
